<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import TriggerBlock from './TriggerBlock.vue';
import FilterBlock from './FilterBlock.vue';
import ActionBlock from './ActionBlock.vue';

const props = defineProps({
  pipelineId: { type: [Number, String], required: true },
  columnId: { type: [Number, String], default: null },
  automation: { type: Object, default: null },
});
const emit = defineEmits(['saved', 'cancel']);

const store = useStore();

const form = ref({
  name: props.automation?.name || '',
  trigger_type: props.automation?.trigger_type || 'card_created',
  trigger_config: props.automation?.trigger_config || {},
  filters: props.automation?.filters || [],
  filter_operator: props.automation?.filter_operator || 'and',
  actions: props.automation?.actions || [],
  run_once_per_card: props.automation?.run_once_per_card ?? true,
  allow_automation_trigger: props.automation?.allow_automation_trigger ?? false,
});

const isSaving = ref(false);

async function save() {
  isSaving.value = true;
  try {
    const payload = {
      pipeline_id: props.pipelineId,
      column_id: props.columnId,
      name: form.value.name,
      trigger_type: form.value.trigger_type,
      trigger_config: form.value.trigger_config,
      filters: form.value.filters,
      filter_operator: form.value.filter_operator,
      actions: form.value.actions,
      run_once_per_card: form.value.run_once_per_card,
      allow_automation_trigger: form.value.allow_automation_trigger,
    };
    if (props.automation?.id) {
      await store.dispatch('kanbanAutomations/update', {
        id: props.automation.id,
        ...payload,
      });
    } else {
      await store.dispatch('kanbanAutomations/create', payload);
    }
    emit('saved');
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <div class="flex flex-col gap-4 max-w-2xl">
    <input
      v-model="form.name"
      :placeholder="$t('KANBAN.AUTOMATIONS.NAME')"
      class="text-base font-semibold text-n-slate-12 bg-transparent border-b border-n-weak focus:border-n-brand focus:outline-none px-0.5 py-1"
    />

    <TriggerBlock
      v-model:trigger-type="form.trigger_type"
      v-model:trigger-config="form.trigger_config"
    />

    <FilterBlock
      v-model:filters="form.filters"
      v-model:operator="form.filter_operator"
    />

    <ActionBlock v-model:actions="form.actions" :pipeline-id="pipelineId" />

    <div class="flex flex-col gap-2 border-t border-n-weak pt-3">
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input
          v-model="form.run_once_per_card"
          type="checkbox"
          class="rounded"
        />
        {{ $t('KANBAN.AUTOMATIONS.RUN_ONCE') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-11">
        <input
          v-model="form.allow_automation_trigger"
          type="checkbox"
          class="rounded"
        />
        {{ $t('KANBAN.AUTOMATIONS.ALLOW_AUTOMATION_TRIGGER') }}
      </label>
    </div>

    <div class="flex items-center gap-2">
      <button
        class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white hover:brightness-110 disabled:opacity-50"
        :disabled="isSaving || !form.name"
        @click="save"
      >
        {{ $t('KANBAN.AUTOMATIONS.SAVE') }}
      </button>
      <button
        class="rounded-lg border border-n-weak px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1"
        @click="emit('cancel')"
      >
        {{ $t('KANBAN.AUTOMATIONS.CANCEL') }}
      </button>
    </div>
  </div>
</template>
